import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type ReactNode, useRef, useState } from 'react';
import { formatDuration } from '../../lib/dateUtils';
import { formatPercent } from '../../lib/format';
import { ElementAttributeTag } from './ElementAttributeTag';

interface CreativeApproachDetailCardProps {
  icon: ReactNode;
  title: string;
  creatives: number | null;
  sectionTitle: string;
  sectionDescription: string;
  categoryTitle: string;
  bullets: string[];
  accentColor: string;
  elements: { feature: string; tag: string }[];
  thumbnailUrl: string | null;
  previewUrl: string | null;
  duration: number | null;
  creativeType: string | null;
  leadingKpi: number | null;
  leadingKpiLabel: string;
}

export function CreativeApproachDetailCard({
  icon,
  title,
  creatives,
  sectionTitle,
  sectionDescription,
  categoryTitle,
  bullets,
  accentColor,
  elements,
  thumbnailUrl,
  previewUrl,
  duration,
  creativeType,
  leadingKpi,
  leadingKpiLabel,
}: CreativeApproachDetailCardProps): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const durationLabel = formatDuration(duration);
  const isVideo = creativeType?.toLowerCase() === 'video';
  const hasVideo = isVideo && Boolean(previewUrl);

  function handleMouseEnter(): void {
    if (!hasVideo) return;
    setIsHovering(true);
    videoRef.current?.play().catch(() => setIsHovering(false));
  }

  function handleMouseLeave(): void {
    setIsHovering(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  return (
    <Box
      sx={{
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: 3.5,
        boxSizing: 'border-box',
        display: 'flex',
        gap: 3,
        height: 260,
        justifyContent: 'space-between',
        p: 3,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, width: '30%' }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'inline-flex',
              height: 40,
              justifyContent: 'center',
              width: 40,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ color: 'text.primary', fontSize: 15, fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 500 }}>
              {creatives ?? '—'} creatives
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>{sectionTitle}</Typography>
          <Typography sx={{ color: 'text.secondary', fontWeight: 500, mt: 1 }}>
            {sectionDescription}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 'auto' }}>
          {elements
            .filter((element) => element.feature && element.tag)
            .slice(0, 2)
            .map((element) => (
              <Box
                key={`${element.feature}-${element.tag}`}
                sx={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <ElementAttributeTag label={element.feature} variant="feature" />
                <ElementAttributeTag label={element.tag} variant="tag" />
              </Box>
            ))}
        </Box>
      </Box>

      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          border: '2px solid',
          borderColor: 'divider',
          borderRadius: 2.5,
          flexShrink: 0,
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          width: '33.3%',
        }}
      >
        {thumbnailUrl || previewUrl ? (
          <>
            {thumbnailUrl && (
              <Box
                component="img"
                src={thumbnailUrl}
                alt={title}
                sx={{
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: 'black',
                  width: '100%',
                }}
              />
            )}
            {hasVideo && previewUrl && (
              <Box
                component="video"
                ref={videoRef}
                src={previewUrl}
                muted
                loop
                playsInline
                preload="none"
                sx={{
                  height: '100%',
                  inset: 0,
                  objectFit: isHovering ? 'contain' : 'cover',
                  backgroundColor: 'black',
                  opacity: isHovering ? 1 : 0,
                  position: 'absolute',
                  transition: 'opacity 150ms ease',
                  width: '100%',
                }}
              />
            )}
          </>
        ) : (
          <Box sx={{ bgcolor: 'background.default', height: '100%', width: '100%' }} />
        )}
        {hasVideo && !isHovering && (
          <Box
            sx={{
              display: 'grid',
              inset: 0,
              placeItems: 'center',
              pointerEvents: 'none',
              position: 'absolute',
            }}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '50%',
                display: 'grid',
                height: 44,
                placeItems: 'center',
                width: 44,
              }}
            >
              <PlayArrowRounded sx={{ color: 'text.primary', fontSize: 22 }} />
            </Box>
          </Box>
        )}
        {durationLabel && (
          <Box
            sx={{
              backgroundColor: 'background.default',
              borderRadius: 1,
              color: 'text.primary',
              fontSize: 12,
              left: 14,
              position: 'absolute',
              px: 1.5,
              py: 0.5,
              top: 14,
            }}
          >
            {durationLabel}
          </Box>
        )}
        {leadingKpi !== null && (
          <Box
            sx={{
              backgroundColor: 'background.default',
              borderRadius: 999,
              color: accentColor,
              fontSize: 12,
              fontWeight: 700,
              position: 'absolute',
              px: 2.5,
              py: 1,
              right: 12,
              top: 12,
            }}
          >
            {formatPercent(leadingKpi, 1)} {leadingKpiLabel}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, width: '33.3%' }}>
        <Box sx={{ alignItems: 'center', color: 'text.primary', display: 'flex', gap: 1, mb: 1.5 }}>
          <ArrowForwardIcon sx={{ color: accentColor, fontSize: 15 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            &quot;{categoryTitle}&quot; Category
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3, pb: 1 }}>
          {bullets.map((bullet) => (
            <Box key={bullet} sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
              <Box sx={{ backgroundColor: accentColor, borderRadius: 999, height: 6, width: 6 }} />
              <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>{bullet}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
